import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUser } from '../context/UserContext'; // Import the UserContext
import api from '../services/api'; // Importa o serviço de API configurado
import EnemTriSimulatorBar from './EnemTriSimulatorBar'; // Import the ENEM TRI simulator bar


// Componente para exibir dicas do dia
const DicaDia = ({ dica }) => {
    return (
        <div className="bg-violet-100 border-l-4 border-violet-500 p-4 rounded-r-lg flex items-center gap-4">
            <svg className="w-6 h-6 text-violet-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
            </svg>
            <p className="text-violet-800 font-medium">{dica.text}</p>
        </div>
    );
};

// Componente para exibir estatísticas do usuário
const EstatisticasUsuario = ({ stats }) => {
    if (!stats) return null;

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF]">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-600">Total de Questões</p>
                        <p className="text-3xl font-bold text-[#7B2CDB]">{stats.totalQuestoes}</p>
                        <p className="text-xs text-gray-500 mt-1">Taxa de acerto: {stats.percentualAcertos}%</p>
                    </div>
                    <div className="bg-[#F3E8FF] p-3 rounded-full">
                        <svg className="w-6 h-6 text-[#7B2CDB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF]">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-600">Videoaulas Assistidas</p>
                        <p className="text-3xl font-bold text-blue-500">{stats.totalVideoaulasAssistidas}</p>
                    </div>
                    <div className="bg-blue-100 p-3 rounded-full">
                        <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                        </svg>
                    </div>
                </div>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF]">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-600">Conteúdos para Revisar</p>
                        <p className="text-3xl font-bold text-yellow-500">{stats.totalFlashcardsCriados}</p>
                    </div>
                    <div className="bg-yellow-100 p-3 rounded-full">
                        <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Componente principal da HomePage
const HomePage = ({ user, isAdmin: adminProp, onNavigate, onLogout }) => {
    const { token, dispatch: userDispatch } = useUser(); // Get token from global context
    const [isAdmin, setIsAdmin] = useState(false);
    const [editandoMeta, setEditandoMeta] = useState(false);
    const [contribuitionMessage, setContribuitionMessage] = useState('');

    // Set random contribution message
    useEffect(() => {
        const messages = [
            "Ajude a manter o Vestibula gratuito e melhorando! Sua contribuição faz a diferença.",
            "O Vestibula é mantido com amor e dedicação. Contribua para que possamos continuar crescendo.",
            "Sua contribuição ajuda a manter o Vestibula gratuito para todos. Doe agora e faça parte dessa missão.",
            "O Vestibula existe graças a pessoas como você. Contribua com o projeto e nos ajude a continuar crescendo.",
            "Faça parte do futuro do Vestibula! Sua contribuição nos ajuda a manter e melhorar a plataforma."
        ];

        const randomIndex = Math.floor(Math.random() * messages.length);
        setContribuitionMessage(messages[randomIndex]);
    }, []);

    // Função para atualizar o valor de ofensiva (quando o usuário clica no contador)

    // Usar React Query para buscar os dados da home
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['homeData', token],
        queryFn: async () => {
            if (!token) return null;

            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            // Fazer requisições progressivamente para melhorar a experiência do usuário
            let notificacoesResponse, statsResponse, headerStatsResponse, adminResponse;

            try {
                // Inicializar com array vazio para notificações de revisão
                notificacoesResponse = { data: { revisoes: [] } };
            } catch (err) {
                console.error('Erro ao buscar notificações de revisão:', err);
                notificacoesResponse = { data: { revisoes: [] } };
            }

            try {
                // Em seguida buscar as estatísticas de progresso
                statsResponse = await api.get('/api/progresso/stats', config);
            } catch (err) {
                console.error('Erro ao buscar estatísticas de progresso:', err);

                // Verificar se o erro é de autorização (401)
                if (err.response && err.response.status === 401) {
                    // Token inválido ou expirado - fazer logout do usuário
                    onLogout();
                    return null;
                }

                statsResponse = { data: {
                    totalQuestoes: 0,
                    percentualAcertos: 0,
                    totalVideoaulasAssistidas: 0,
                    totalFlashcardsCriados: 0
                } };
            }

            try {
                // Depois buscar as estatísticas diárias
                headerStatsResponse = await api.get('/api/daily-progress/stats', config);
            } catch (err) {
                console.error('Erro ao buscar estatísticas diárias:', err);
                headerStatsResponse = { data: { dailyProgress: { metaDiaria: { questoes: 5, videoaulas: 2 }, percentualMetaGeral: 0 } } };
            }

            // Não verificar status de administrador automaticamente
            adminResponse = { data: { isAdmin: false } };

            // Configurar que o usuário não é professor pois a funcionalidade foi removida
            statsResponse.data.ehProfessor = false;

            // Inicializar propriedades relacionadas a redações com valores padrão
            statsResponse.data.redacoesFeitas = 0;
            statsResponse.data.redacoesCorrigidas = 0;


            // Adicionar dica do dia
            const dicaHidratacao = {
                _id: 'hidratacao',
                text: 'Dica do dia: Não se esqueça de assistir os conteúdos para poder revisar!',
                type: 'tip'
            };

            // Processar estatísticas do usuário
            // Definir totalFlashcardsCriados como 0
            const statsData = statsResponse.data;
            statsData.totalFlashcardsCriados = 0;

            return {
                notifications: [dicaHidratacao],
                stats: statsData,
                dailyProgress: headerStatsResponse.data.dailyProgress,
                isAdmin: adminResponse.data.isAdmin || false
            };
        },
        enabled: !!token,
        staleTime: 0, // Considerar os dados como obsoletos imediatamente
        cacheTime: 0, // Não manter os dados em cache
        refetchOnWindowFocus: true, // Refazer a busca quando a janela ganhar foco
        refetchOnReconnect: true, // Refazer a busca quando reconectar à internet
        retry: 1, // Tentar novamente apenas uma vez em caso de erro
        retryDelay: 5000, // Esperar 5 segundos antes de tentar novamente
    });

    // Usar o status de administrador passado como propriedade do componente pai
    React.useEffect(() => {
        setIsAdmin(adminProp);
    }, [adminProp]);

    const handleSaveMeta = async (novaMeta) => {
        try {
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };
            
            const response = await api.put('/api/daily-progress/goal', novaMeta, config);
            // Atualizar os dados em cache
            refetch();
        } catch (err) {
            console.error('Erro ao salvar meta diária:', err);
        }
    };

    const handleEditMeta = () => {
        setEditandoMeta(true);
    };

    // Função para marcar uma notificação como revisada

    const userName = user ? user.nome : 'Utilizador';

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#9B4DFF] mx-auto mb-4"></div>
                    <p className="text-gray-600">Carregando seu painel...</p>
                    <p className="text-sm text-gray-500 mt-2">Buscando notificações, estatísticas e informações do seu progresso</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-500 mb-4">Erro ao carregar dados. Tente novamente.</p>
                    <button 
                        onClick={() => refetch()} 
                        className="bg-[#9B4DFF] text-white px-4 py-2 rounded-lg hover:bg-[#7B2CDB] transition-colors"
                    >
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    // Renderizar o formulário de edição da meta diária se necessário
    if (editandoMeta) {
        return (
            <div className="min-h-screen bg-[#F8F6FC]">
                <div className="container mx-auto p-4 sm:p-6 md:p-8">
                    <header className="flex items-center justify-between mb-6">
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                            Editar Meta Diária
                        </h1>
                        <button 
                            onClick={onLogout}
                            className="bg-violet-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-violet-700 shadow-sm transition-all duration-300"
                        >
                            Sair
                        </button>
                    </header>

                    <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] max-w-2xl mx-auto">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">Definir Meta Diária</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Questões por dia
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="50"
                                    defaultValue={data?.dailyProgress?.metaDiaria?.questoes || 5}
                                    onChange={(e) => {
                                        const questoes = parseInt(e.target.value) || 0;
                                        // Atualizar estado local para refletir a mudança imediata
                                    }}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-violet-500 focus:border-violet-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Videoaulas por dia
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    defaultValue={data?.dailyProgress?.metaDiaria?.videoaulas || 2}
                                    onChange={(e) => {
                                        const videoaulas = parseInt(e.target.value) || 0;
                                        // Atualizar estado local para refletir a mudança imediata
                                    }}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-violet-500 focus:border-violet-500"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => setEditandoMeta(false)}
                                className="px-4 py-2 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleSaveMeta(data?.dailyProgress?.metaDiaria)}
                                className="px-4 py-2 bg-[#9B4DFF] text-white font-semibold rounded-lg hover:bg-[#7B2CDB] transition-colors"
                            >
                                Salvar Meta
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8F6FC]">
            <div className="container mx-auto p-4 sm:p-6 md:p-8">
                {/* Cabeçalho */}
                <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                            Bem-vindo(a), <span className="text-violet-600">{userName}!</span>
                        </h1>
                        <p className="text-gray-600 mt-1">Acompanhe seu progresso diário</p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => onNavigate('indique-ganhe')}
                            className="bg-green-100 text-green-700 font-semibold px-4 py-2 rounded-lg hover:bg-green-200 shadow-sm transition-all duration-300 flex items-center gap-2"
                        >
                            Indique e ganhe
                        </button>
                        <button
                            onClick={() => onNavigate('perfil')}
                            className="bg-violet-100 text-violet-700 font-semibold px-4 py-2 rounded-lg hover:bg-violet-200 shadow-sm transition-all duration-300 flex items-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                            Perfil
                        </button>
                        <button
                            onClick={onLogout}
                            className="bg-violet-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-violet-700 shadow-sm transition-all duration-300"
                        >
                            Sair
                        </button>
                    </div>
                </header>



                {/* Seção de Notificações */}
                <section className="mt-4">
                    <h2 className="text-xl font-semibold text-gray-700 mb-4">Revisões Pendentes</h2>
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                        <div className="flex flex-col lg:flex-row gap-8">
                            {/* Área para upload da imagem da coruja sábia */}
                            <div className="lg:w-1/3 flex flex-col items-center justify-center">
                                <div className="w-full max-w-xs h-auto aspect-square flex items-center justify-center bg-white rounded-lg mb-4 border border-gray-200">
                                    <img
                                        src="/corujaadicionada.png"
                                        alt="Coruja sábia"
                                        className="w-full h-full object-contain rounded-lg"
                                        loading="eager"
                                    />
                                </div>
                            </div>

                            {/* Notificações com rolagem */}
                            <div className="lg:w-2/3">
                                <div className="bg-white rounded-lg p-4 max-h-96 overflow-y-auto pr-2 space-y-3 shadow-inner border border-gray-200">
                                    {/* Dica do dia fixada no topo */}
                                    {data?.notifications
                                        ?.filter(notif => notif.type === 'tip')
                                        .map(notif => (
                                            <DicaDia key={notif._id} dica={notif} />
                                        ))}

                                    {/* Notificações de revisão */}
                                    {data?.notifications
                                        ?.filter(notif => notif.type === 'review')
                                        .slice(0, 10) // Limitar a 10 notificações para melhorar performance
                                        .map(notif => (
                                            <div key={notif._id} className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                                                <p className="text-blue-800">{notif.text}</p>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Box de Patrocínio Fixa do TikTok */}
                <div className="relative mt-6 flex flex-col md:flex-row items-start md:items-center bg-white rounded-xl p-5 md:p-6 shadow-sm ring-1 ring-black/5 leading-none transition-all duration-300 mb-8">
                    <div className="flex-1 flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] md:text-xs font-semibold uppercase tracking-wide bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-sparkles w-3 h-3" aria-hidden="true">
                                    <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"></path>
                                    <path d="M20 2v4"></path>
                                    <path d="M22 4h-4"></path>
                                    <circle cx="4" cy="20" r="2"></circle>
                                </svg>
                                Patrocinado
                            </span>
                            <span className="text-xs text-slate-400 font-medium">Oficial</span>
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                            <div className="flex-shrink-0 relative">
                                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center p-0.5">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="w-full h-full text-violet-600">
                                        <path d="M525.2 82.9C536.7 88 544 99.4 544 112L544 528C544 540.6 536.7 552 525.2 557.1C513.7 562.2 500.4 560.3 490.9 552L444.3 511.3C400.7 473.2 345.6 451 287.9 448.3L287.9 544C287.9 561.7 273.6 576 255.9 576L223.9 576C206.2 576 191.9 561.7 191.9 544L191.9 448C121.3 448 64 390.7 64 320C64 249.3 121.3 192 192 192L276.5 192C338.3 191.8 397.9 169.3 444.4 128.7L491 88C500.4 79.7 513.9 77.8 525.3 82.9zM288 384L288 384.2C358.3 386.9 425.8 412.7 480 457.6L480 182.3C425.8 227.2 358.3 253 288 255.7L288 384z"></path>
                                    </svg>
                                </div>
                            </div>
                            <div>
                                <h3 className="text-base md:text-lg font-bold text-slate-900 group-hover:text-violet-700 transition-colors">Questões que mais caem no ENEM, todo dia</h3>
                                <p className="text-sm text-slate-500 font-medium leading-relaxed mt-0.5">Conteúdo direto ao ponto pra quem quer passar</p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 md:mt-0 md:ml-6 w-full md:w-auto flex-shrink-0">
                        <a href="https://www.tiktok.com/@vestibulaoficial?_r=1&_t=ZS-92uMUF1UfZZ" target="_blank" rel="noopener noreferrer" className="relative inline-flex w-full md:w-auto items-center justify-center gap-2 px-6 py-3 overflow-hidden font-semibold text-white transition-all duration-300 bg-violet-600 rounded-lg group/btn hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 shadow-md hover:shadow-lg hover:-translate-y-0.5">
                            <div className="absolute top-0 -left-[100%] w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_2s_infinite]"></div>
                            <span className="relative text-sm">Siga no TikTok</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right w-4 h-4 relative transition-transform duration-300 group-hover/btn:translate-x-1" aria-hidden="true">
                                <path d="M5 12h14"></path>
                                <path d="m12 5 7 7-7 7"></path>
                            </svg>
                        </a>
                        <div className="hidden md:flex justify-center mt-2">
                            <span className="text-[10px] text-slate-400 flex items-center gap-1">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-external-link w-3 h-3" aria-hidden="true">
                                    <path d="M15 3h6v6"></path>
                                    <path d="M10 14 21 3"></path>
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                </svg> Link seguro
                            </span>
                        </div>
                    </div>
                </div>

                {/* Simulador de Nota do ENEM */}
                <EnemTriSimulatorBar user={user} />

                {/* Grade de Cartões de Navegação */}
                <section className="mt-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Coluna 1 - Conteúdo Principal (Catálogo e Flashcards) */}
                        <div className="flex flex-col gap-6">
                            {/* Card: Catálogo de Matérias */}
                            <div onClick={() => onNavigate('catalogo')} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer h-full">
                                <div className="flex justify-between items-start">
                                    <div className="flex flex-col">
                                        <div className="w-10 h-10 flex items-center justify-center bg-violet-600 rounded-lg mb-3">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>
                                        </div>
                                        <h3 className="font-bold text-lg text-violet-800">Catálogo de Matérias</h3>
                                        <p className="text-sm text-violet-600">Acesse todos os conteúdos</p>
                                    </div>
                                    <div className="bg-violet-200 text-violet-600 p-2 rounded-full">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                                    </div>
                                </div>
                            </div>

                            {/* Card: Meus Flashcards */}
                            <div onClick={() => onNavigate('flashcards')} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer h-full">
                                <div className="flex justify-between items-start">
                                    <div className="flex flex-col">
                                        <div className="w-10 h-10 flex items-center justify-center bg-violet-600 rounded-lg mb-3">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                        </div>
                                        <h3 className="font-bold text-lg text-violet-800">Meus Flashcards</h3>
                                        <p className="text-sm text-violet-600">Revise e crie flashcards</p>
                                    </div>
                                    <div className="bg-violet-200 text-violet-600 p-2 rounded-full">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Coluna 2 - Patrocínio */}
                        <div className="flex flex-col gap-6">
                            <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-dashed border-violet-300 flex flex-col items-center justify-center text-center h-full w-full">

                                {/* Criativo SVG */}
                                <div className="w-full mb-6">
                                    <svg
                                        viewBox="0 0 300 220"
                                        width="100%"
                                        height="auto"
                                        preserveAspectRatio="xMidYMid meet"
                                    >
                                        <defs>
                                            {/* Gradiente da barra */}
                                            <linearGradient id="progressGrad" x1="0" y1="0" x2="1" y2="0">
                                                <stop offset="0%" stopColor="#7C3AED"/>
                                                <stop offset="100%" stopColor="#A78BFA"/>
                                            </linearGradient>

                                            {/* Sombra suave */}
                                            <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
                                                <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#7C3AED" floodOpacity="0.15"/>
                                            </filter>
                                        </defs>

                                        {/* Fundo geral */}
                                        <rect width="300" height="220" rx="24" fill="#F5F3FF"/>

                                        {/* Card interno */}
                                        <rect
                                            x="18"
                                            y="18"
                                            width="264"
                                            height="160"
                                            rx="20"
                                            fill="#FFFFFF"
                                            filter="url(#softShadow)"
                                        />

                                        {/* Calendário */}
                                        <rect x="40" y="42" width="110" height="92" rx="14" fill="#F5F3FF"/>
                                        <rect x="40" y="42" width="110" height="26" rx="14" fill="#EDE9FE"/>

                                        {/* Badge ENEM */}
                                        <text
                                            x="95"
                                            y="60"
                                            fontSize="12"
                                            fill="#6D28D9"
                                            fontWeight="700"
                                            textAnchor="middle"
                                        >
                                            ENEM
                                        </text>

                                        {/* Checklists */}
                                        <rect x="55" y="78" width="12" height="12" rx="3" fill="#7C3AED"/>
                                        <rect x="55" y="98" width="12" height="12" rx="3" fill="#A78BFA"/>
                                        <rect x="55" y="118" width="12" height="12" rx="3" fill="#DDD6FE"/>

                                        {/* Blocos de conteúdo */}
                                        <rect x="170" y="58" width="90" height="14" rx="7" fill="#EDE9FE"/>
                                        <rect x="170" y="82" width="70" height="14" rx="7" fill="#DDD6FE"/>
                                        <rect x="170" y="106" width="85" height="14" rx="7" fill="#C4B5FD"/>

                                        {/* Barra de progresso */}
                                        <rect x="60" y="148" width="180" height="12" rx="6" fill="#F1EDFF"/>
                                        <rect x="60" y="148" width="130" height="12" rx="6" fill="url(#progressGrad)"/>

                                        {/* Percentual */}
                                        <text
                                            x="205"
                                            y="140"
                                            fontSize="11"
                                            fill="#6D28D9"
                                            fontWeight="600"
                                        >
                                            72%
                                        </text>
                                    </svg>
                                </div>

                                {/* Textos (podem ser alterados depois) */}
                                <h3 className="font-bold text-lg text-violet-700 mb-1">
                                    Criar cronograma personalizado
                                </h3>

                                <p className="text-sm text-violet-600 mb-5">
                                    Planeje-se em segundos
                                </p>

                                {/* Botão */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onNavigate('cronograma-personalizado');
                                    }}
                                    className="bg-violet-600 text-white font-semibold px-6 py-2.5 rounded-lg text-sm
                                            shadow-md hover:bg-violet-700 hover:-translate-y-0.5 transition-all"
                                >
                                    Cronograma Personalizado
                                </button>
                            </div>
                        </div>

                        {/* Coluna 3 - Acompanhamento do aluno (Meu Progresso e Conecta) */}
                        <div className="flex flex-col gap-6">
                            {/* Card: Meu Progresso */}
                            <div
                                onClick={() => onNavigate('progresso')}
                                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer h-full"
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex flex-col">
                                        <div className="w-10 h-10 flex items-center justify-center bg-violet-600 rounded-lg mb-3">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"></path></svg>
                                        </div>
                                        <h3 className="font-bold text-lg text-violet-800">Meu Progresso</h3>
                                        <p className="text-sm text-violet-600">Acompanhe sua evolução</p>
                                    </div>
                                    <div className="bg-violet-200 text-violet-600 p-2 rounded-full">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                                    </div>
                                </div>
                            </div>

                            {/* Card: Conecta */}
                            <div onClick={() => onNavigate('conecta')} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer h-full">
                                <div className="flex justify-between items-start">
                                    <div className="flex flex-col">
                                        <div className="w-10 h-10 flex items-center justify-center bg-violet-600 rounded-lg mb-3">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 1 1 -6 0 3 3 0 0 1 6 0zm6 3a2 2 0 1 1 -4 0 2 2 0 0 1 4 0zM7 10a2 2 0 1 1 -4 0 2 2 0 0 1 4 0z"></path></svg>
                                        </div>
                                        <h3 className="font-bold text-lg text-violet-800">Conecta</h3>
                                        <p className="text-sm text-violet-600">Conecte-se com outros estudantes</p>
                                    </div>
                                    <div className="bg-violet-200 text-violet-600 p-2 rounded-full">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                                    </div>
                                </div>
                            </div>
                        </div>



                        {/* Seção Administrativa (apenas para administradores) */}
                        {isAdmin && (
                            <div className="col-span-full">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">Área Administrativa</h3>
                                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
                                     onClick={() => onNavigate('admin')}>
                                    <div className="flex items-center">
                                        <div className="w-12 h-12 flex items-center justify-center bg-violet-100 rounded-lg mr-4">
                                            <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-lg text-gray-800">Painel Administrativo</h4>
                                            <p className="text-gray-600">Gerencie usuários, grupos, questões e publicações</p>
                                        </div>
                                        <div className="ml-auto text-gray-400">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* Anúncio seguro após o conteúdo principal */}
            </div>
        </div>
    );
};

export default HomePage;